import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { GetEventsQueryParams, eventApi } from '@/api/eventApi'
import { getPageByUrl } from '@/api/vpApi'
import { Event } from '@/types/event'
import { PaginatedResponse } from '@/types/pagination'
import { objectToQueryString } from '@/utils/url'

export const eventQueryKeys = {
  all: ['events'] as const,
  list: (appId: string, queryParamFilters: GetEventsQueryParams) =>
    [...eventQueryKeys.all, 'list', appId, queryParamFilters] as const,
  listInfinite: (appId: string, queryParamFilters: Record<string, string>) =>
    [...eventQueryKeys.all, 'list-infinite', appId, queryParamFilters] as const,
  details: (eventId: string, trackingIds?: string) =>
    [...eventQueryKeys.all, 'details', eventId, trackingIds] as const,
  eventDownloadUrl: (appId: string, taskId: string) =>
    [...eventQueryKeys.all, 'pdf-url', appId, taskId] as const,
  eventListFilters: (appId: string, tagSources?: string[]) =>
    [...eventQueryKeys.all, 'list-filters', appId, tagSources] as const
}

export const useGetCameraEvents = (
  appId: string,
  streamId: string,
  limit = 5
) => {
  return useQuery({
    queryKey: eventQueryKeys.list(appId, { stream_ids: streamId, limit }),
    queryFn: () => eventApi.getEvents(appId, { stream_ids: streamId, limit })
  })
}

export const useGetEventsInfinite = (
  appId: string,
  sortingAndFilterParams: Record<string, string>
) => {
  const queryString = objectToQueryString({
    limit: 20,
    ...sortingAndFilterParams
  })

  const initialPageParam = `/v1/applications/${appId}/events/?${queryString}`

  return useInfiniteQuery({
    queryKey: eventQueryKeys.listInfinite(appId, sortingAndFilterParams),
    queryFn: ({ pageParam }) =>
      getPageByUrl<PaginatedResponse<Event[]>>(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage.next ?? undefined
    },
    initialPageParam
  })
}

export const useDeleteEvent = () => {
  return useMutation({
    mutationFn: eventApi.deleteEvent
  })
}

export const useAddEventNote = () => {
  return useMutation({
    mutationFn: eventApi.addEventNote
  })
}

export const useDeleteEventNote = () => {
  return useMutation({
    mutationFn: eventApi.deleteEventNote
  })
}

export const useGetEventDetails = (
  appId: string,
  eventId: string,
  trackingIds?: string
) => {
  return useQuery({
    queryKey: eventQueryKeys.details(eventId, trackingIds),
    queryFn: () => eventApi.getEventDetails({ appId, eventId, trackingIds })
  })
}

export const useGenerateEventsAs = () => {
  return useMutation({
    mutationFn: eventApi.generateEventsAs
  })
}

export const useFetchDownloadEventUrl = (
  appId: string,
  taskId: string,
  enabled: boolean
) => {
  return useQuery({
    queryKey: eventQueryKeys.eventDownloadUrl(appId, taskId),
    queryFn: () => eventApi.fetchDownloadEventUrl({ appId, taskId }),
    enabled
  })
}

export const useGetEventListFilters = (
  appId: string,
  tagSources?: string[]
) => {
  return useQuery({
    queryKey: eventQueryKeys.eventListFilters(appId, tagSources),
    queryFn: () => {
      const params = tagSources?.length
        ? { tag_sources: tagSources.join(',') }
        : undefined
      return eventApi.getEventListFilters(appId, params)
    }
  })
}
